import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, capitalize, Dialog, Grid } from '@mui/material';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import * as TStyle from 'commons/components/Table/Table.styles';
import { InboundTaskItemStatusEnum } from 'commons/enums';
import { IInboundInvoice, IInboundTaskItem } from 'commons/types';
import { RowTitleProps } from 'commons/types/listPageData.interface';
import { formatDate } from 'commons/utils/formatDate';
import { DataTable } from 'pages/dashboard/commons/components';
import {
  getInboundInvoiceItemListAction,
  resetInboundTaskItemAssignment,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { setAllInboundInvoiceItemList } from 'redux-stores/reducers/dashboard.reducer';
import { AppDispatch } from 'redux-stores/store';

import S from '../styles/InboundTaskItemDialogPage.style';

const rowTitles: RowTitleProps[] = [
  { title: 'SKU Code' },
  { title: 'Name' },
  { title: 'UOM' },
  { title: 'FOC' },
  { title: 'QTY' },
  { title: 'Received QTY' },
  { title: 'Receive Type' },
  { title: 'Status' },
];

const TableRow: React.FC<{
  data: IInboundTaskItem;
  index: number;
  handleOpenDialog: () => void;
  setSelectedItem: (item: IInboundTaskItem) => void;
}> = ({ data, index, handleOpenDialog, setSelectedItem }) => {
  const totalReceiveActual = data.receive_actual.reduce(
    (receivedQty, receiveActual) => receivedQty + receiveActual.quantity,
    0,
  );
  return (
    <TStyle.TableRow>
      <TStyle.TableColumn
        style={{ width: '10%' }}
        data-testid={`InboundTaskItemSKU-${index + 1}`}
      >
        {data.sku_code}
      </TStyle.TableColumn>
      <TStyle.TableColumn
        style={{ width: '20%' }}
        data-testid={`InboundTaskItemName-${index + 1}`}
      >
        {data.name}
      </TStyle.TableColumn>
      <TStyle.TableColumn
        style={{ width: '20%' }}
        data-testid={`InboundTaskItemUOM-${index + 1}`}
      >
        {data.uom}
      </TStyle.TableColumn>
      <TStyle.TableColumn
        style={{ width: '5%' }}
        data-testid={`InboundTaskItemFOC-${index + 1}`}
      >
        {data.foc ? '✔' : null}
      </TStyle.TableColumn>
      <TStyle.TableColumn
        style={{ width: '5%' }}
        data-testid={`InboundTaskItemQuantity-${index + 1}`}
      >
        {data.quantity}
      </TStyle.TableColumn>
      <TStyle.TableColumn
        style={{ width: '8%' }}
        data-testid={`InboundTaskItemReceivedQuantity-${index + 1}`}
      >
        {totalReceiveActual}
      </TStyle.TableColumn>
      <TStyle.TableColumn
        style={{ width: '8%' }}
        data-testid={`InboundTaskItemReceiveType-${index + 1}`}
      >
        {[
          InboundTaskItemStatusEnum.RECEIVED,
          InboundTaskItemStatusEnum.APPROVED,
          InboundTaskItemStatusEnum.REJECTED,
        ].includes(data.status) ? (
          <Box
            component="span"
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: '12px',
              fontSize: '0.75rem',
              fontWeight: 600,
              backgroundColor:
                totalReceiveActual === data.quantity ? '#e3f2fd' : '#f5f5f5',
              color:
                totalReceiveActual === data.quantity ? '#1565c0' : '#757575',
            }}
          >
            {totalReceiveActual === data.quantity ? 'Full' : 'Partial'}
          </Box>
        ) : (
          '-'
        )}
      </TStyle.TableColumn>
      <TStyle.TableColumn
        style={{ width: '10%' }}
        data-testid={`InboundTaskItemStatus-${index + 1}`}
      >
        {capitalize(data.status.replace(/-/g, ' '))}
      </TStyle.TableColumn>
      <TStyle.TableColumn
        style={{ width: '35%' }}
        data-testid={`InboundTaskItemAction-${index + 1}`}
      >
        {[
          InboundTaskItemStatusEnum.ASSIGNED,
          InboundTaskItemStatusEnum.RECEIVING,
        ].includes(data.status) && (
          <Button
            data-testid={`buttonInboundTaskItemResetAssignment-${index + 1}`}
            onClick={() => {
              handleOpenDialog();
              setSelectedItem(data);
            }}
            variant="outlined"
          >
            Reset Assignment
          </Button>
        )}
      </TStyle.TableColumn>
    </TStyle.TableRow>
  );
};

export const InboundInvoiceItemDialogPage: React.FC<{
  inboundInvoice: IInboundInvoice | null;
  onClose: () => void;
}> = ({ inboundInvoice, onClose }) => {
  /* CONSTANTS */
  const dispatch = useDispatch<AppDispatch>();
  const { inboundInvoiceItemList } = useSelector(
    (state: RootReducerInterface) => state.dashboard,
  );
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] =
    useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<IInboundTaskItem | null>(
    null,
  );

  const handleOpenDialog = (): void => {
    setOpenConfirmationDialog(true);
  };

  const handleCloseDialog = (): void => {
    setOpenConfirmationDialog(false);
  };

  /* HOOKS */
  useEffect(() => {
    if (inboundInvoice !== null) {
      // fetch inbound invoice items
      dispatch(
        getInboundInvoiceItemListAction({
          inboundTaskNumber: inboundInvoice.inbound_task_number,
          invoiceNumber: inboundInvoice.invoice_number,
        }),
      );
    }
  }, [inboundInvoice, dispatch]);

  useEffect(() => {
    if (inboundInvoiceItemList.list.length === 0) {
      setShowDialog(false);
      if (
        inboundInvoiceItemList.inbound_task_number !== '-' &&
        inboundInvoiceItemList.invoice_number !== '-'
      ) {
        dispatch(
          setAllInboundInvoiceItemList({
            inbound_task_number: '-',
            invoice_number: '-',
            list: [],
          }),
        );
      }
    } else {
      setShowDialog(true);
    }
  }, [dispatch, inboundInvoiceItemList]);

  return (
    <>
      <Dialog
        open={!!inboundInvoice && showDialog}
        onClose={onClose}
        PaperProps={{
          sx: { maxWidth: '80vw', width: '80vw', maxHeight: '80vh' },
        }}
      >
        {/* Title */}
        <S.TitleWrapper container>
          <Grid item xs={12}>
            <b>Inbound Invoice Item Detail</b>
          </Grid>
        </S.TitleWrapper>

        {/* Content Document Information */}
        <S.DocumentInformationWrapper container rowGap={3}>
          <Grid item xs={3}>
            <S.DocumentInfoTitle>Document Number</S.DocumentInfoTitle>
            <S.DocumentInfoValue>
              <b>{inboundInvoice?.inbound_task_number || '-'}</b>
            </S.DocumentInfoValue>
          </Grid>
          <Grid item xs={2}>
            <S.DocumentInfoTitle>Invoice Number</S.DocumentInfoTitle>
            <S.DocumentInfoValue>
              <b>{inboundInvoice?.invoice_number || '-'}</b>
            </S.DocumentInfoValue>
          </Grid>
          <Grid item xs={2}>
            <S.DocumentInfoTitle>Invoice Date</S.DocumentInfoTitle>
            <S.DocumentInfoValue>
              <b>
                {inboundInvoice
                  ? formatDate(inboundInvoice.invoice_date, 'dd/LL/yyyy')
                  : '-'}
              </b>
            </S.DocumentInfoValue>
          </Grid>
          <Grid item xs={3}>
            <S.DocumentInfoTitle>Receiver</S.DocumentInfoTitle>
            <S.DocumentInfoValue>
              <b>
                {inboundInvoice?.receiver
                  ? inboundInvoice.receiver.split('@')[0]
                  : '-'}
              </b>
            </S.DocumentInfoValue>
          </Grid>
          <Grid item xs={2}>
            <S.DocumentInfoTitle>Status</S.DocumentInfoTitle>
            <S.DocumentInfoValue>
              <b>
                {capitalize(
                  inboundInvoice?.integration_status.replace(/-/g, ' ') || '-',
                )}
              </b>
            </S.DocumentInfoValue>
          </Grid>
        </S.DocumentInformationWrapper>

        {/* Inbound Invoice Item List Tables */}
        <S.TableSection>
          <DataTable rowTitles={rowTitles} hideCheckbox>
            <TStyle.TableBody>
              {inboundInvoiceItemList.list.map((item, index) => (
                <TableRow
                  data={item}
                  index={index}
                  handleOpenDialog={handleOpenDialog}
                  setSelectedItem={setSelectedItem}
                />
              ))}
            </TStyle.TableBody>
          </DataTable>
        </S.TableSection>
      </Dialog>
      <ConfirmationDialog
        title="Reset Assignment"
        content={`Are you sure to reset assignment for item ${selectedItem?.sku_code} in purchase order ${inboundInvoice?.inbound_task_number}?`}
        onClose={() => handleCloseDialog()}
        openModal={openConfirmationDialog}
        onConfirm={async () => {
          if (selectedItem && inboundInvoice) {
            await dispatch(resetInboundTaskItemAssignment(selectedItem));
            dispatch(
              getInboundInvoiceItemListAction({
                inboundTaskNumber: inboundInvoice.inbound_task_number,
                invoiceNumber: inboundInvoice.invoice_number,
              }),
            );
          }
        }}
      />
    </>
  );
};
